import { Box, Button, Checkbox, Modal, TextField, Typography, useTheme } from "@mui/material";
import SendIcon from "../../svg/SendIcon";
import { useEffect, useState } from "react";

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  options: string[];
  otherLabel?: string;
  onSubmit?: (feedback: { reason: string; otherText?: string }) => void;
  actions?: {
    label: string;
    color?: "primary" | "error" | "success" | "inherit";
    variant?: "contained" | "outlined" | "text";
    icon?: React.ReactNode;
    disabledWhenInvalid?: boolean;
    onClick: (feedback: { reason: string; otherText?: string }) => void;
  }[];
}


const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  options,
  otherLabel = "Other",
  onSubmit,
  actions,
}) => {
  const theme = useTheme();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [otherText, setOtherText] = useState("");

  const isInvalid =
    !selectedReason || (selectedReason === otherLabel && !otherText.trim());

  const feedback = {
    reason: selectedReason ?? "",
    otherText: selectedReason === otherLabel ? otherText : undefined,
  };

  useEffect(() => {
    if (!open) {
      setSelectedReason(null);
      setOtherText("");
    }
  }, [open]);

  const defaultHandleSubmit = () => {
    if (!onSubmit || isInvalid) return;
    onSubmit(feedback);
    setSelectedReason(null);
    setOtherText("");
    onClose();
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      slotProps={{
        backdrop: {
          style: {
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          },
        }
      }}  
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          minWidth: {xs:'90dvw',md:300},
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          alignItems: "start",
        }}
      >
        {/* Title + subtitle */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5">{title}</Typography>
          {subtitle && <Typography variant="subtitle1">{subtitle}</Typography>}
        </Box>

        {/* Options list */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            border: `2px solid ${theme.palette.divider}`,
            borderRadius: 2.5,
            width: "100%",
          }}
        >
          {options.map((opt, index) => (
            <Box
              key={opt}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1,
                paddingY: 1,
                borderBottom: `2px solid ${theme.palette.divider}`,
                width: "100%",
              }}
            >
              <Checkbox
                checked={selectedReason === opt}
                onChange={() => setSelectedReason(opt)}
                style={{ marginRight: "8px" }}
              />
              <Typography variant="body1">{opt}</Typography>
            </Box>
          ))}

          {/* Other option */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              gap: 1,
              mb: 1,
              paddingY: 1,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <Checkbox
                checked={selectedReason === otherLabel}
                onChange={() => setSelectedReason(otherLabel)}
                style={{ marginRight: "8px" }}
              />
              <Typography variant="body1">{otherLabel}</Typography>
            </Box>
            <TextField
              sx={{ ml: 10, mb: 1, width: "-webkit-fill-available", pr: 2}}
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              label="Reason"
              variant="outlined"
              size="small"
              onClick={() => setSelectedReason(otherLabel)}
            />
          </Box>
        </Box>

        {/* Buttons */}
        <Box sx={{ width: "100%", display: "flex", justifyContent: "end", gap: 1 }}>
          {actions
            ? actions.map((action) => (
                <Button
                  key={action.label}
                  onClick={() => {
                    action.onClick(feedback);
                  }}
                  variant={action.variant || "contained"}
                  color={action.color || "primary"}
                  disabled={action.disabledWhenInvalid && isInvalid}
                >
                  {action.icon}
                  {action.label}
                </Button>
              ))
            : (
              <Button
                onClick={defaultHandleSubmit}
                variant="contained"
                sx={{ backgroundColor: theme.palette.primary.main }}
                disabled={isInvalid}
              >
                <SendIcon
                  color={
                    isInvalid
                      ? theme.palette.action.imageHoverDisabledText
                      : theme.palette.primary.contrastText
                  }
                />
                Submit
              </Button>
            )}
        </Box>
      </Box>
    </Modal>
  );
};

export default FeedbackModal;
